(function () {
  class QuickBuyDrawer extends HTMLElement {
    constructor() {
      super();
      this.variants = [];
      this.selectedOptions = {};
      this.matchedVariant = null;
      this.escHandler = (e) => {
        if (e.key === 'Escape' && !this.hasAttribute('hidden')) this.close();
      };
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
     
      try {
        const raw = this.dataset.variants || '[]';
        this.variants = JSON.parse(raw);
        // Normalize: ensure every variant has a proper options array
        this.variants = this.variants.map((v) => ({
          ...v,
          options: Array.isArray(v.options)
            ? v.options.map(String)
            : [v.option1, v.option2, v.option3].filter(Boolean).map(String),
        }));
      } catch (e) {
        console.error('[QuickBuyDrawer] variants parse error:', e);
        this.variants = [];
      }

      this.panel          = this.querySelector('.quick-buy-drawer__panel');
      this.atcBtn         = this.querySelector('[data-quick-buy-add]');
      this.priceEl        = this.querySelector('[data-quick-buy-price]');
      this.priceCurrentEl = this.querySelector('[data-quick-buy-price-current]');
      this.priceCompareEl = this.querySelector('[data-quick-buy-price-compare]');
      this.optionGroups   = this.querySelectorAll('.quick-buy-drawer__option');

      this.optionGroups.forEach((group) => {
        const idx = parseInt(group.dataset.optionIndex, 10);
        group.querySelectorAll('.quick-buy-drawer__chip').forEach((chip) => {
          chip.addEventListener('click', () => this.selectOption(idx, chip));
        });
      });

      this.querySelectorAll('[data-quick-buy-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );

      if (this.atcBtn) {
        this.atcBtn.addEventListener('click', () => this.addToCart());
      }

      this.initDefaults();
    }

    isMobileViewport() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    ensurePortalPlaceholder() {
      if (this._portalPlaceholder) return;
      this._portalPlaceholder = document.createComment('quick-buy-drawer');
      this._homeParent = this.parentNode;
      this._homeParent.insertBefore(this._portalPlaceholder, this);
    }

    moveToBody() {
      this.ensurePortalPlaceholder();
      if (this.parentNode !== document.body) {
        document.body.appendChild(this);
      }
    }

    restoreToHome() {
      if (this._portalPlaceholder && this._portalPlaceholder.parentNode) {
        this._portalPlaceholder.parentNode.insertBefore(this, this._portalPlaceholder);
      }
    }

    initDefaults() {
      // Auto-select + hide single-value option groups
      this.optionGroups.forEach((group, idx) => {
        const chips = group.querySelectorAll('.quick-buy-drawer__chip');
        if (chips.length === 1) {
          const chip = chips[0];
          chip.setAttribute('aria-checked', 'true');
          chip.classList.add('quick-buy-drawer__chip--selected');
          this.selectedOptions[idx] = String(chip.dataset.optionValue);
          group.hidden = true;
        }
      });

      // Auto-select first available size
      this.optionGroups.forEach((group, idx) => {
        if (group.hidden) return;
        if (this.selectedOptions[idx] !== undefined) return;

        const labelEl = group.querySelector('.quick-buy-drawer__option-label');
        const label = labelEl ? labelEl.textContent.toLowerCase() : '';
        if (!label.includes('size')) return;

        const chips = Array.from(group.querySelectorAll('.quick-buy-drawer__chip'));
        const firstAvailable = chips.find((chip) => {
          const value = String(chip.dataset.optionValue);
          return this.variants.some(
            (v) => v.options[idx] === value && v.available
          );
        });
        if (firstAvailable) {
          firstAvailable.setAttribute('aria-checked', 'true');
          firstAvailable.classList.add('quick-buy-drawer__chip--selected');
          this.selectedOptions[idx] = String(firstAvailable.dataset.optionValue);
        }
      });

      this.updateAvailable();
    }

    open() {
      document.querySelectorAll('quick-buy-drawer:not([hidden])').forEach((drawer) => {
        if (drawer !== this && typeof drawer.close === 'function') drawer.close();
      });

      this._cardWrapper = this.closest('.card-wrapper');
      this._cardWrapper?.classList.add('quick-buy-drawer-host--open');
      this._portaled = this.isMobileViewport();
      if (this._portaled) this.moveToBody();
      this.removeAttribute('hidden');
      if (this._portaled) document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.escHandler);
      requestAnimationFrame(() => {
        this.classList.add('quick-buy-drawer--open');
      });
    }

    close() {
      this.classList.remove('quick-buy-drawer--open');
      if (this._portaled) document.body.style.overflow = '';
      document.removeEventListener('keydown', this.escHandler);
      setTimeout(() => {
        this.setAttribute('hidden', '');
        if (this._portaled) {
          this.restoreToHome();
          this._portaled = false;
        }
        this._cardWrapper?.classList.remove('quick-buy-drawer-host--open');
        this._cardWrapper = null;
      }, 280);
    }

    selectOption(optionIndex, chip) {
      const group = chip.closest('.quick-buy-drawer__option');
      group.querySelectorAll('.quick-buy-drawer__chip').forEach((c) => {
        c.setAttribute('aria-checked', 'false');
        c.classList.remove('quick-buy-drawer__chip--selected');
      });
      chip.setAttribute('aria-checked', 'true');
      chip.classList.add('quick-buy-drawer__chip--selected');
      this.selectedOptions[optionIndex] = String(chip.dataset.optionValue);
      this.updateAvailable();
    }

    markUnavailable(optionIndex) {
      const group = this.optionGroups[optionIndex];
      if (!group) return;
      const chips = group.querySelectorAll('.quick-buy-drawer__chip');
      chips.forEach((chip) => {
        const value = String(chip.dataset.optionValue);
        // console.log
        const anyAvailable = this.variants.some((v) => {
          if (!Array.isArray(v.options)) return false;
          if (v.options[optionIndex] !== value) return false;
          if (!v.available) return false;
          for (const [idx, val] of Object.entries(this.selectedOptions)) {
            const i = parseInt(idx, 10);
            if (i === optionIndex) continue;
            if (v.options[i] !== val) return false;
          }
          return true;
        });
        chip.classList.toggle('quick-buy-drawer__chip--soldout', !anyAvailable);
      });
    }

    updateAvailable() {
      const totalOptions = this.optionGroups.length;
      const selectedCount = Object.keys(this.selectedOptions).length;

      this.optionGroups.forEach((_, idx) => this.markUnavailable(idx));

      if (!this.atcBtn) return;

      if (selectedCount < totalOptions) {
        this.matchedVariant = null;
        this.atcBtn.disabled = true;
        this.atcBtn.textContent = this.atcBtn.dataset.defaultLabel || 'Add to cart';
        return;
      }

      this.matchedVariant = this.variants.find((v) =>
        Object.entries(this.selectedOptions).every(
          ([idx, val]) => v.options[parseInt(idx, 10)] === val
        )
      );

      if (!this.matchedVariant) {
        this.atcBtn.disabled = true;
        this.atcBtn.textContent = 'Unavailable';
        return;
      }

      if (!this.matchedVariant.available) {
        this.atcBtn.disabled = true;
        this.atcBtn.textContent = 'Sold out';
        return;
      }

      this.atcBtn.disabled = false;
      this.atcBtn.textContent = this.atcBtn.dataset.defaultLabel || 'Add to cart';

      if (this.priceCurrentEl && this.matchedVariant.price_formatted) {
        this.priceCurrentEl.textContent = this.matchedVariant.price_formatted;
      }
      if (this.priceCompareEl) {
        const hasCompare =
          this.matchedVariant.compare_at_price &&
          this.matchedVariant.compare_at_price > this.matchedVariant.price;
        if (hasCompare) {
          this.priceCompareEl.textContent = this.matchedVariant.compare_at_price_formatted;
          this.priceCompareEl.removeAttribute('hidden');
          this.priceEl?.classList.add('quick-buy-drawer__price--on-sale');
        } else {
          this.priceCompareEl.setAttribute('hidden', '');
          this.priceEl?.classList.remove('quick-buy-drawer__price--on-sale');
        }
      }
    }

    async refreshFromProductJson(handle) {

      try {
        // console.log(this , "re-renderbefore")
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error(`Product fetch failed: ${res.status}`);
        const  product  = await res.json();
        // console.log(product , "product.variants")
        const variants = product.variants.map((v) => ({
          id: v.id,
          available: v.available,
          price: v.price,
          price_formatted: this.#formatMoney(v.price),
          compare_at_price: v.compare_at_price ?? 0,
          compare_at_price_formatted: this.#formatMoney(v.compare_at_price ?? 0),
          options: [v.option1, v.option2, v.option3].filter(Boolean).map(String),
        }));
        
        const optionsHtml = product.options.map((option, idx) => {
          const optionName = option.name ?? option;
          const values = [...new Set(product.variants.map((v) => v[`option${idx + 1}`]))];
          const chipsHtml = values.map((val) =>
            `<button type="button" class="quick-buy-drawer__chip" role="radio" aria-checked="false" data-option-value="${this.#escHtml(val)}">
               <span class="quick-buy-drawer__chip-text">${this.#escHtml(val)}</span>
             </button>`
          ).join('');
          return `
            <div class="quick-buy-drawer__option" data-option-index="${idx}">
              <p class="quick-buy-drawer__option-label">${this.#escHtml(optionName)}</p>
              <div class="quick-buy-drawer__chips" role="radiogroup" aria-label="${this.#escHtml(optionName)}">
                ${chipsHtml}
              </div>
            </div>`;
        }).join('');

        const featuredMedia = product.images?.[0];
        const thumbHtml = featuredMedia
          ? `<img class="quick-buy-drawer__thumb" src="${product.featured_image}" alt="" width="80" height="80" loading="lazy">`
          : '';

        const price = product.variants[0]?.price ?? 0;
        const compareAtPrice = product.variants[0]?.compare_at_price ?? 0;
        const onSale = compareAtPrice > price;

        const innerHTML = `
          <div class="quick-buy-drawer__scrim" data-quick-buy-close></div>
          <div class="quick-buy-drawer__panel" role="dialog" aria-modal="true" aria-labelledby="quick-buy-title-${product.id}">
            <button class="quick-buy-drawer__close" type="button" data-quick-buy-close aria-label="Close">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18"/>
              </svg>
            </button>
            <div class="quick-buy-drawer__head">
              ${thumbHtml}
              <div class="quick-buy-drawer__meta">
                <h2 id="quick-buy-title-${product.id}" class="quick-buy-drawer__title">${this.#escHtml(product.title)}</h2>
                <p class="quick-buy-drawer__price${onSale ? ' quick-buy-drawer__price--on-sale' : ''}" data-quick-buy-price>
                  <span class="quick-buy-drawer__price-current" data-quick-buy-price-current>${this.#formatMoney(price)}</span>
                  <s class="quick-buy-drawer__price-compare" data-quick-buy-price-compare${onSale ? '' : ' hidden'}>${this.#formatMoney(compareAtPrice)}</s>
                </p>
              </div>
            </div>
            ${optionsHtml}
            <button class="quick-buy-drawer__atc" type="button" data-quick-buy-add data-default-label="Add to cart" disabled>
              Add to cart
            </button>
          </div>`;

        const replacement = document.createElement('quick-buy-drawer');
        replacement.className = this.className;
        this.hasAttribute('hidden') && replacement.setAttribute('hidden', '');
        replacement.setAttribute('data-product-id', product.id);
        replacement.setAttribute('data-variants', JSON.stringify(variants));
        
        replacement.innerHTML = innerHTML;


        const placeholder = this._portalPlaceholder;
        if (placeholder?.parentNode) {
          placeholder.parentNode.insertBefore(replacement, placeholder);
          placeholder.remove();
        } else if (this.parentNode) {
          this.parentNode.insertBefore(replacement, this);
        } else {
          document.body.appendChild(replacement);
        }

        this.remove();
        return replacement;
      } catch (err) {
        console.error('[QuickBuyDrawer] refreshFromProductJson:', err);
        return null;
      }
    }

    #formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, window.theme?.moneyFormat ?? '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    }

    #escHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    async addToCart() {
      if (!this.matchedVariant || !this.matchedVariant.available) return;

      const miniCart = document.querySelector('mini-cart');
      const sections = miniCart
        ? miniCart.getSectionsToRender().map((s) => s.id)
        : [];

      this.atcBtn.classList.add('loading');
      this.atcBtn.disabled = true;

      try {
        const res = await fetch(window.theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: this.matchedVariant.id,
            quantity: 1,
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();

        if (data.status === 422) {
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:error', {
              detail: { errorMessage: data.description },
            })
          );
          // this.atcBtn.textContent = 'Try again';
        } else {
          if (miniCart) miniCart.renderContents(data);
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:added', { detail: { product: data } })
          );
          this.close();
        }
      } catch (e) {
        console.error(e);
        this.atcBtn.textContent = 'Try again';
      } finally {
        this.atcBtn.classList.remove('loading');
        this.atcBtn.disabled = false;
      }
    }
  }

  customElements.define('quick-buy-drawer', QuickBuyDrawer);

  class QuickBuyButton extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        // console.log(this)
        const wrapper = this.closest('.card-wrapper') || this.closest(".images_variant_container");
        if (!wrapper) return;
        const drawer = wrapper.querySelector('quick-buy-drawer');
        if (drawer && typeof drawer.open === 'function') {
        
        drawer.open();

        }
      });
    }
  }

  customElements.define('quick-buy-button', QuickBuyButton);
})();